import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AuthService } from "./auth.service";
import { DatePipe } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { DataElement, ServiceCallInfo } from "../models/common";
import {
  Invoice,
  InvoiceReportsSearchParams,
  Receipt,
  BalanceRow,
} from "../models/invoices.model";
import { Observable } from "rxjs";
import { ServiceOptions } from "../models/service-options";
import { map, catchError, take } from "rxjs/operators";
import { ServiceMetaData } from "../models/service-meta.model";
import {
  OperationReport,
  ReportParam,
  ReportParamTypeEnum,
  ReportRunStatusResult,
} from "../models/operation_reports.model";
import {
  ClinicStoplight,
  KpiCartesian,
  KpiCaseDelays,
  KpiCircle,
  KpiConfig,
  KpiCumulIncome,
  KpiCycleDuration,
  KpiDonut,
  KpiFixTrends,
  KpiIncomingCases,
  KpiItemRadar,
  KpiPlantOrderStatus,
  KpiProfitTrends,
  KpiReturnTrends,
  KpiScanAnalog,
  KpiStream,
  KpiTryInTrends,
  LabKpiModel,
  StoplightMetric,
} from "../models/kpi.model";
import { tabList } from "../models/tab.model";
import { Store } from "@ngrx/store";
import {
  GET_CLIENT_BALANCE,
  GET_CLIENT_INVOICES,
  GET_CLIENT_RECEIPTS,
  GET_KPI_CONFIG,
  GET_LABS_KPI,
  GET_OPERATION_REPORTS,
  GET_TOTAL_DEBT,
} from "../ngrx/ngrx-actions";

@Injectable({
  providedIn: "root",
})
export class ReportsService {
  private mockupDataUrl = "assets/mock/";

  constructor(
    private readonly store: Store,
    private http: HttpClient,
    private authService: AuthService,
    private datePipe: DatePipe,
    private translateService: TranslateService
  ) { }

  getClientInvoices(
    searchParams: InvoiceReportsSearchParams
  ): Observable<Invoice[]> {
    let response = new Observable<Invoice[]>();
    let serviceName = "get_client_invoices";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (searchParams.clinic) params["clinicId"] = searchParams.clinic.id;
    if (searchParams.fromDate)
      params["fromDate"] = searchParams.fromDate.to_DD_MM_YYYY();
    if (searchParams.toDate)
      params["toDate"] = searchParams.toDate.to_DD_MM_YYYY();
    if (searchParams.documentId) params["invId"] = searchParams.documentId;

    if (searchParams.rangeFrom || searchParams.rangeFrom === 0)
      params["fromRec"] = searchParams.rangeFrom;
    if (searchParams.rangeTo) params["toRec"] = searchParams.rangeTo;
    if (searchParams.searchId || searchParams.searchId === 0)
      params["searchId"] = searchParams.searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractClientInvoices(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractClientInvoices(res: any, info: ServiceCallInfo): Invoice[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: Invoice[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    if (data) {
      let rowTag = data.row;
      let metaTag = data.metadata;
      metaData.totalRecords = metaTag.totRec;
      metaData.fromRecord = metaTag.fromRec;
      metaData.toRecord = metaTag.toRec;
      metaData.searchId = metaTag.searchId;

      if (rowTag) {
        let arr = new Array().concat(rowTag);
        arr.forEach((i) => {
          let row = new Invoice();
          row.invId = i.invId;
          row.invDate = this.authService.fromXMLDate(i.invDate);
          if (row.invDate)
            row.invDateFormatted = this.datePipe.transform(
              row.invDate,
              "dd/MM/yyyy"
            );
          row.invTotSum = (i.invTotSum as String)
            .replace("₪", "")
            .trim() as any as number;
          row.invHaktzaa = i.invHaktzaa ?? null;
          row.clinicId = i.clinicId;
          row.clinicDescr = i.clinicDescr;
          result.push(row);
        });
      }

      this.store.dispatch(
        GET_TOTAL_DEBT({
          data: data.summary ? data.summary.totalBalDept || 0 : 0,
        })
      );
    }

    this.store.dispatch(
      GET_CLIENT_INVOICES({
        data: result,
        medataData: metaData,
      })
    );

    return result;
  }

  getClientReceipts(
    searchParams: InvoiceReportsSearchParams
  ): Observable<Receipt[]> {
    let response = new Observable<Receipt[]>();
    let serviceName = "get_client_receipts";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (searchParams.clinic) params["clinicId"] = searchParams.clinic.id;
    if (searchParams.fromDate)
      params["fromDate"] = searchParams.fromDate.to_DD_MM_YYYY();
    if (searchParams.toDate)
      params["toDate"] = searchParams.toDate.to_DD_MM_YYYY();
    if (searchParams.documentId) params["rcptId"] = searchParams.documentId;

    if (searchParams.rangeFrom || searchParams.rangeFrom === 0)
      params["fromRec"] = searchParams.rangeFrom;
    if (searchParams.rangeTo) params["toRec"] = searchParams.rangeTo;
    if (searchParams.searchId || searchParams.searchId === 0)
      params["searchId"] = searchParams.searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractClientReceipts(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractClientReceipts(res: any, info: ServiceCallInfo): Receipt[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: Receipt[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    if (data) {
      let rowTag = data.row;
      let metaTag = data.metadata;
      metaData.totalRecords = metaTag.totRec;
      metaData.fromRecord = metaTag.fromRec;
      metaData.toRecord = metaTag.toRec;
      metaData.searchId = metaTag.searchId;

      if (rowTag) {
        let arr = new Array().concat(rowTag);
        arr.forEach((i) => {
          let row = new Receipt();
          row.rcptId = i.rcptId;
          row.rcptDate = this.authService.fromXMLDate(i.rcptDate);
          if (row.rcptDate)
            row.rcptDateFormatted = this.datePipe.transform(
              row.rcptDate,
              "dd/MM/yyyy"
            );
          row.rcptTotSum = (i.rcptTotSum as String)
            .replace("₪", "")
            .trim() as any as number;
          row.invId = i.invNum;
          row.clinicId = i.clinicId;
          row.clinicDescr = i.clinicDescr;
          result.push(row);
        });
      }

      this.store.dispatch(
        GET_TOTAL_DEBT({
          data: data.summary ? data.summary.totalBalDept || 0 : 0,
        })
      );
    }

    this.store.dispatch(
      GET_CLIENT_RECEIPTS({
        data: result,
        medataData: metaData,
      })
    );

    return result;
  }

  getClientBalance(
    searchParams: InvoiceReportsSearchParams
  ): Observable<BalanceRow[]> {
    let response = new Observable<BalanceRow[]>();
    let serviceName = "get_client_balance";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (searchParams.clinic) params["clinicId"] = searchParams.clinic.id;
    if (searchParams.fromDate)
      params["fromDate"] = searchParams.fromDate.to_DD_MM_YYYY();
    if (searchParams.toDate)
      params["toDate"] = searchParams.toDate.to_DD_MM_YYYY();
    if (searchParams.documentId) params["balRef"] = searchParams.documentId;

    if (searchParams.rangeFrom || searchParams.rangeFrom === 0)
      params["fromRec"] = searchParams.rangeFrom;
    if (searchParams.rangeTo) params["toRec"] = searchParams.rangeTo;
    if (searchParams.searchId || searchParams.searchId === 0)
      params["searchId"] = searchParams.searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractClientBalance(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractClientBalance(res: any, info: ServiceCallInfo): BalanceRow[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: BalanceRow[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    if (data) {
      let rowTag = data.row;
      let metaTag = data.metadata;
      metaData.totalRecords = metaTag.totRec;
      metaData.fromRecord = metaTag.fromRec;
      metaData.toRecord = metaTag.toRec;
      metaData.searchId = metaTag.searchId;

      if (rowTag) {
        let arr = new Array().concat(rowTag);
        arr.forEach((i) => {
          let row = new BalanceRow();
          row.balRef = i.balRef;
          row.balDate = this.authService.fromXMLDate(i.balDate);
          if (row.balDate)
            row.balDateFormatted = this.datePipe.transform(
              row.balDate,
              "dd/MM/yyyy"
            );
          row.balValueDate = this.authService.fromXMLDate(i.balValueDate);
          if (row.balValueDate)
            row.balValueDateFormatted = this.datePipe.transform(
              row.balValueDate,
              "dd/MM/yyyy"
            );
          row.balDebtSum = (i.balDebtSum as String)
            .replace("₪", "")
            .trim() as any as number;
          row.balCreditSum = (i.balCreditSum as String)
            .replace("₪", "")
            .trim() as any as number;
          row.balance = i.balance || 0;
          row.balDocKind = i.balDocKind;
          row.clinicId = i.clinicId;
          row.clinicDescr = i.clinicDescr;
          result.push(row);
        });
      }

      this.store.dispatch(
        GET_TOTAL_DEBT({
          data: data.summary ? data.summary.totalBalDept || 0 : 0,
        })
      );
    }

    this.store.dispatch(
      GET_CLIENT_BALANCE({
        data: result,
        medataData: metaData,
      })
    );

    return result;
  }

  public getInvoicePdfUrl(invNum: number): Observable<string> {
    return this.requestPdfUrl("brosh.GenerateInvoicePdf", { inhNum: invNum });
  }

  public getReceiptPdfUrl(rcptId: number): Observable<string> {
    return this.requestPdfUrl("brosh.GenerateReceiptPdf", { rcptId });
  }

  public getBalancePdfUrl(
    clinicId: number,
    fromDate?: Date,
    toDate?: Date
  ): Observable<string> {
    const data: any = { clinicId };
    if (fromDate) data.fromDate = fromDate.to_DD_MM_YYYY();
    if (toDate) data.toDate = toDate.to_DD_MM_YYYY();
    return this.requestPdfUrl("brosh.GenerateBalancePdf", data);
  }

  // The finance PDFs are ALG methods: the worker renders the document and
  // answers with a presigned url to it.
  private requestPdfUrl(serviceName: string, data: object): Observable<string> {
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    Object.assign(params, data);

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, true);
    return this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((res) => this.extractPdfUrl(res, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );
  }

  private extractPdfUrl(res: any, info: ServiceCallInfo): string {
    this.authService.extractRequestStatusNew(info, res);
    let data = res.reply.data;
    if (data) {
      return data.s3Url;
    }
    return null;
  }

  getOperationReports(): Observable<OperationReport[]> {
    let response = new Observable<OperationReport[]>();
    let serviceName = "get_operation_reports";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractOperationReports(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractOperationReports(
    res: any,
    info: ServiceCallInfo
  ): OperationReport[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: OperationReport[] = [];
    let data = res.reply.requestResult.data;

    if (data && data.reports) {
      let arr = new Array().concat(data.reports);
      arr.forEach((i) => {
        let report = new OperationReport();
        report.reportId = i.reportId;
        report.reportType = i.reportCategory;
        report.reportDescr = i.reportDescr;

        if (i.params) {
          let pArr = new Array().concat(i.params);
          pArr.forEach((p) => {
            let param = new ReportParam();
            param.paramCode = p.paramCode;
            param.paramDescr = p.paramDescr;
            param.type = p.type;
            param.listType = p.listType;
            param.maxListLength = p.maxListLength;
            param.default = p.default;
            param.requiredSw = p.requiredSw;
            if (param.type == ReportParamTypeEnum.CUSTOM_LIST && p.customList) {
              let lArr = new Array().concat(p.customList);
              lArr.forEach((e) => {
                param.customList.push(
                  new DataElement(e.valueCode, e.valueDescr)
                );
              });
            }
            report.params.push(param);
          });
        }

        result.push(report);
      });
    }

    this.store.dispatch(
      GET_OPERATION_REPORTS({
        data: result,
      })
    );

    return result;
  }

  createReportRun(report: OperationReport): Observable<number> {
    let response = new Observable<number>();
    let serviceName = "create_report_run";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    const paramList = [];
    report.params.forEach((p) => {
      let value: any;
      switch (p.type) {
        case ReportParamTypeEnum.CUSTOM_LIST:
          if (p.valueElement) {
            value = p.valueElement.id;
          } else if (p.valueElementList && p.valueElementList.length) {
            value = p.valueElementList.map((i) => i.id).join("~");
          }
          break;
        case ReportParamTypeEnum.LIST:
          if (p.valueElement) {
            value = p.valueElement.id;
          } else if (p.valueElementList && p.valueElementList.length) {
            value = p.valueElementList.map((i) => i.id).join("~");
          }
          break;
        case ReportParamTypeEnum.DATE:
          if (p.valueDate) value = p.valueDate.to_DD_MM_YYYY();
          break;
        case ReportParamTypeEnum.NUMBER:
          if (p.valueNumber != null)
            value = Number.parseInt(p.valueNumber.toString());
          break;
        case ReportParamTypeEnum.TEXT:
          if (p.valueText != null) value = p.valueText;
          break;
        case ReportParamTypeEnum.TEXTAREA:
          if (p.valueText != null) value = p.valueText;
          break;
        default:
          break;
      }
      paramList.push({
        paramCode: p.paramCode,
        value: value,
      });
    });

    params["params"] = paramList;
    params["reportId"] = report.reportId;
    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractCreateReportRun(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  extractCreateReportRun(res: any, info: ServiceCallInfo): number {
    this.authService.extractRequestStatusNew(info, res);
    let data = res.reply.requestResult.data;
    if (data) {
      return data.runId;
    }

    return 0;
  }

  getReportRunStatus(runId: number): Observable<ReportRunStatusResult> {
    let serviceName = "get_report_run_status";

    let response = new Observable<ReportRunStatusResult>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params["runId"] = runId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => {
          return this.extractGetProcessStatus(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  extractGetProcessStatus(
    res: any,
    info: ServiceCallInfo
  ): ReportRunStatusResult {
    this.authService.extractRequestStatusNew(info, res);
    let data = res.reply.requestResult.data;
    let result = new ReportRunStatusResult();

    if (data) {
      result.status = data.status;
      result.errorMsg = data.statusMessage;
      result.outFileUrl = data.outFileUrl;
    }

    return result;
  }

  runReport(runId: number): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "brosh.RunReport";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["runId"] = runId;

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, false);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  GetLabKPI(filter: string, filterType: string): Observable<LabKpiModel> {
    let response = new Observable<LabKpiModel>();
    let serviceName = "get_lab_kpi_json";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["filter"] = filter;
    params["filterType"] = filterType;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    info.alertWhenOffline = false;
    info.online = navigator.onLine;

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractLabKpi(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  extractKpiStream(tag, streamCode: string): KpiStream {
    let s = new KpiStream();
    s.streamCode = streamCode;
    s.dates = Array().concat(tag.date);
    s.valCnt = Array().concat(tag.valCnt);
    s.unitDescr = Array().concat(tag.unit);
    s.arrowType = tag.arrowType;
    if (s.arrowType != null) s.arrowType.forEach((i) => (i = i?.toUpperCase()));
    s.monthName = tag.monthName;
    s.monthDescr = tag.monthName;
    s.valSharePct = tag.valSharePct;
    s.trendPct = tag.trendPct;
    s.avgPrice = tag.valAvgPrice;
    s.itemGroup = tag.itemGroup;
    s.itemGroupDescr = tag.itemGroupDescr;
    return s;
  }

  extractKpiDonut(tag): KpiDonut {
    let s = new KpiDonut();
    s.categoryCode = tag.categoryCode;
    s.categoryDescr = tag.categoryDescr;
    s.colorCode = tag.colorCode;
    s.valPct = tag.valPct;
    s.valCnt = tag.valCnt;
    return s;
  }

  extractKpiCircle(tag): KpiCircle {
    let s = new KpiCircle();
    s.cnt = tag.cnt;
    s.circleColor = tag.circleColor?.toUpperCase();
    s.deltaPct = tag.deltaPct;
    s.deltaColor = tag.deltaColor?.toUpperCase();
    s.deltaCnt = tag.deltaCnt;
    s.label = tag.label;
    s.arrowType = tag.arrowType?.toUpperCase();
    return s;
  }

  private extractLabKpi(res: any, info: ServiceCallInfo): LabKpiModel {
    this.authService.extractRequestStatusNew(info, res);
    let result: LabKpiModel = new LabKpiModel();
    let data = res.reply.requestResult.data;

    if (data) {
      if (data.incomingCases) {
        if (data.incomingCases.incomingCases) {
          let kpi = new KpiIncomingCases();
          let el = data.incomingCases.incomingCases;
          kpi.cnt = el.cnt;
          kpi.label = el.label;
          kpi.arrowType = el.arrowType;
          kpi.circleColor = el.circleColor;
          kpi.deltaPct = el.deltaPct;
          result.allIncomingCases = kpi;
        }
        if (data.incomingCases.incomingDigital) {
          let kpi = new KpiIncomingCases();
          let el = data.incomingCases.incomingDigital;
          kpi.cnt = el.cnt;
          kpi.label = el.label;
          kpi.arrowType = el.arrowType;
          kpi.circleColor = el.circleColor;
          kpi.deltaPct = el.deltaPct;
          result.digitalIncomingCases = kpi;
        }
      }

      if (data.cumulIncome) {
        let kpi = new KpiCumulIncome();
        let el = data.cumulIncome;
        kpi.current = this.extractKpiStream(el.current, "CUR_MONTH");
        kpi.past = this.extractKpiStream(el.prev, "PAST_MONTH");
        kpi.past2 = this.extractKpiStream(el.prev2, "PAST_MONTH2");
        if (el.breakeven)
          kpi.breakeven = this.extractKpiStream(el.breakeven, "BREAK_EVEN");
        result.cumulIncome = kpi;
      }

      if (data.caseDelays) {
        let kpi = new KpiCaseDelays();
        let el = data.caseDelays;
        kpi.plantCnt = el.productionDelays.cnt;
        kpi.plantCircleColor = el.productionDelays.circleColor;
        kpi.labCnt = el.labDelays.cnt;
        kpi.labCircleColor = el.labDelays.circleColor;
        result.caseDelays = kpi;
      }

      if (data.plantOrderStatus) {
        let kpi = new KpiPlantOrderStatus();
        let el = data.plantOrderStatus;
        kpi.plantId = el.plantId;
        kpi.plantName = el.plantName;
        kpi.totCnt = el.totCnt;
        kpi.regularCnt = el.regularCnt;
        kpi.criticalCnt = el.criticalCnt;
        kpi.overdueCnt = el.overdueCnt;
        kpi.regularPct = el.regularPct;
        kpi.criticalPct = el.criticalPct;
        kpi.overduePct = el.overduePct;
        result.plantOrderStatus = kpi;
      }

      if (data.onlineClinics) {
        let kpi = this.extractKpiDonut(data.onlineClinics);
        result.onlineUsers = kpi;
      }

      if (data.invoiceSummDonut) {
        let kpi = this.extractKpiDonut(data.invoiceSummDonut);
        result.invoices = kpi;
      }

      if (data.deliveryRequestsBar) {
        let kpi = this.extractKpiDonut(data.deliveryRequestsBar);
        result.deliveryReq = kpi;
      }

      if (data.clientStoplight) {
        let el = data.clientStoplight;
        result.greenClients = this.extractKpiCircle(el.green);
        result.redClients = this.extractKpiCircle(el.red);
        result.yellowClients = this.extractKpiCircle(el.yellow);
      }

      if (data.abandonClinics) {
        let el = data.abandonClinics;
        result.abandonClients = this.extractKpiCircle(el);
      }

      if (data.failedCustomerAcuisition) {
        let el = data.failedCustomerAcuisition;
        result.acuisitionFailClients = this.extractKpiCircle(el);
      }

      // if (data.invoices) {
      //   let el = data.invoices;
      //   result.activeClients = this.extractKpiCircle(el.activeClinics);
      //   result.abandonClients = this.extractKpiCircle(el.abandonClinics);
      // }

      if (data.profitTrends) {
        let kpi = new KpiProfitTrends();
        let el = data.profitTrends;
        kpi.income = this.extractKpiStream(el.income, "CUR_INCOME");
        kpi.incomePrev = this.extractKpiStream(el.incomePrev, "PAST_INCOME");
        kpi.orderPlacement = this.extractKpiStream(el.orderPlacement, "CUR_ORDER_PLACEMENT");
        if (el.profit) {
          kpi.profit = this.extractKpiStream(el.profit, "CUR_PROFIT");
          kpi.profitPrev = this.extractKpiStream(el.profitPrev, "PAST_PROFIT");
        }

        result.profitTrends = kpi;
      }

      if (data.cycleDurationCurrent || data.cycleDurationCompleted) {
        let kpi = new KpiCycleDuration();
        if (data.cycleDurationCurrent)
          kpi.current = this.extractKpiDonut(data.cycleDurationCurrent);
        if (data.cycleDurationCompleted)
          kpi.completed = this.extractKpiDonut(data.cycleDurationCompleted);
        result.cycleDuration = kpi;
      }

      if (data.scanAnalog) {
        let kpi = new KpiScanAnalog();
        let el = data.scanAnalog;
        kpi.scans = this.extractKpiStream(el.scan, "DIGITAL");
        kpi.analog = this.extractKpiStream(el.analog, "ANALOG");
        result.scanAnalog = kpi;
      }

      if (data.itemComposition) {
        let kpi = new KpiItemRadar();
        let el = data.itemComposition;
        kpi.m1 = this.extractKpiStream(el[0], "M1");
        kpi.m2 = this.extractKpiStream(el[1], "M2");
        kpi.m3 = this.extractKpiStream(el[2], "M3");
        result.itemRadar = kpi;
      }
      if (data.tryInTrends) {
        let kpi = new KpiTryInTrends();
        let el = data.tryInTrends;
        if (el.tryIn) {
          kpi.tryIn = this.extractKpiStream(el.tryIn, "TRY_IN");
          kpi.tryInPrev = this.extractKpiStream(el.tryInPrev, "TRY_IN_PREV");
        }
        if (el.tryInClinic) {
          kpi.tryInClinic = this.extractKpiStream(
            el.tryInClinic,
            "TRY_IN_CLINIC"
          );
        }
        if (el.tryInClinicPrev) {
          kpi.tryInClinicPrev = this.extractKpiStream(
            el.tryInClinicPrev,
            "TRY_IN_CLINIC_PREV"
          );
        }
        result.tryInTrends = kpi;
      }

      if (data.fixTrends) {
        let kpi = new KpiFixTrends();
        let el = data.fixTrends;
        if (el.fixes) {
          kpi.fix = this.extractKpiStream(el.fixes, "FIX");
          kpi.fixPrev = this.extractKpiStream(el.fixesPrev, "FIX_PREV");
        }
        if (el.fixesClinic) {
          kpi.fixClinic = this.extractKpiStream(el.fixesClinic, "FIX_CLINIC");
        }
        if (el.fixesClinicPrev) {
          kpi.fixClinicPrev = this.extractKpiStream(
            el.fixesClinicPrev,
            "FIX_CLINIC_PREV"
          );
        }
        result.fixTrends = kpi;
      }

      if (data.returnTrends) {
        let kpi = new KpiReturnTrends();
        let el = data.returnTrends;
        if (el.returns) {
          kpi.return = this.extractKpiStream(el.returns, "RETURNS");
          kpi.returnPrev = this.extractKpiStream(
            el.returnsPrev,
            "RETURNS_PREV"
          );
        }
        if (el.returnsClinic) {
          kpi.returnClinic = this.extractKpiStream(
            el.returnsClinic,
            "RETURNS_CLINIC"
          );
        }
        if (el.returnsClinicPrev) {
          kpi.returnClinicPrev = this.extractKpiStream(
            el.returnsClinicPrev,
            "RETURNS_CLINIC_PREV"
          );
        }
        result.returnTrends = kpi;
      }

      if (data.employeePerformance) {
        let el = data.employeePerformance;
        result.employeePerformance = [];
        if (el.datasets) {
          let dataSetsArr = new Array().concat(el.datasets);
          dataSetsArr.forEach((ds) => {
            let cartesian = new KpiCartesian();
            cartesian.code = ds.code;
            cartesian.display = ds.display;
            cartesian.datapointCode = Array().concat(ds.datapointcode || []);
            cartesian.datapointDescr = Array().concat(ds.datapointdescr || []);
            cartesian.valCnt = Array().concat(ds.valcnt || []);
            result.employeePerformance.push(cartesian);
          });
        }
      }

      if (data.clinicStoplight) {
        let el = data.clinicStoplight;
        result.slColor = el.color;
        result.slScore = el.score;
        if (el.metrics) {
          let metricsArr = new Array().concat(el.metrics);
          metricsArr.forEach((m) => {
            let metric = new StoplightMetric();
            metric.metricCode = m.metricCode;
            metric.descr = m.metricDescr;
            metric.score = m.metricScore;
            metric.color = m.metricColor;

            result.slMetrics.push(metric);
          });
        }
      }
    }

    this.store.dispatch(
      GET_LABS_KPI({
        data: result,
      })
    );

    return result;
  }

  getCliStoplightDrill(
    color: string,
    agentCode: string = undefined,
    filter: string = undefined,
    filterType: string = undefined
  ): Observable<ClinicStoplight[]> {
    let response = new Observable<ClinicStoplight[]>();
    let serviceName = "get_cli_stoplight_drill";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["color"] = color;
    params["filter"] = filter;
    params["filterType"] = filterType;

    if (agentCode) {
      params["agentCode"] = agentCode;
    }

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractCliStoplightDrill(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractCliStoplightDrill(
    res: any,
    info: ServiceCallInfo
  ): ClinicStoplight[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: ClinicStoplight[] = [];
    let data = res.reply.requestResult.data;

    if (data) {
      let rowTag = data.clinic;
      if (rowTag) {
        let arr = new Array().concat(rowTag);
        arr.forEach((i) => {
          let row = new ClinicStoplight();
          row.clinicId = i.clinicId;
          row.clinicDescr = i.clinicDescr;
          row.snoozeSw = i.snoozeSw;
          row.snoozeStartdate = i.snoozeStartdate;
          row.snoozeNote = i.note;

          let metricsTag = i.metrics;
          if (metricsTag && metricsTag.metric) {
            let metricsArr = new Array().concat(metricsTag.metric);
            metricsArr.forEach((m) => {
              let metric = new StoplightMetric();
              metric.metricCode = m.metricCode;
              metric.descr = m.descr;
              metric.score = m.score;
              metric.color = m.color;

              row.metrics.push(metric);
            });
          }

          result.push(row);
        });
      }
    }

    return result;
  }

  getKPIConfig(): Observable<KpiConfig[]> {
    let response = new Observable<KpiConfig[]>();
    let serviceName = "get_kpi_config";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        take(1),
        map((data) => {
          return this.extractKPIConfig(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  private extractKPIConfig(res, info: ServiceCallInfo): KpiConfig[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: KpiConfig[] = [];
    let data = res.reply.requestResult.data;

    if (data) {

      let arr = new Array().concat(data);
      arr.forEach((i) => {
        let row = new KpiConfig(i.filterType, i.featureName);
        result.push(row);
      });
    }


    this.store.dispatch(
      GET_KPI_CONFIG({
        data: result,
      })
    );

    return result;
  }
}
